import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { notificationService } from '../services/notificationService';
import { getApiBaseUrl } from '../lib/api-base-url';
import { toast } from 'sonner';

export enum UserRole {
  OPERATOR = 'OPERATOR',
  MANAGER = 'MANAGER',
  ACCOUNTANT = 'ACCOUNTANT',
  ADMIN = 'ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
}

interface User {
  id: number;
  name: string;
  email: string;
  tenantId: number;
  role: UserRole;
}

export interface Tenant {
  id: number;
  name: string;
  logoUrl?: string | null;
  primaryColor: string;
  secondaryColor: string;
  language: string;
  companyAddress?: string;
  vatNumber?: string;
  pec?: string;
  fiscalCode?: string;
  bankName?: string;
  iban?: string;
  bic?: string;
  commercialEntityId?: number | null;
}

interface AuthContextType {
  isAuthReady: boolean;
  isAuthenticated: boolean;
  user: User | null;
  tenant: Tenant | null;
  applyAuthPayload: (payload: { access_token: string; user: User; tenant?: Tenant | null }) => void;
  login: (token: string) => Promise<void>;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateTenant: (updates: Partial<Tenant>) => void;
  refreshTenant: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const API_URL = getApiBaseUrl();
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  // Setup axios 401 interceptor for session expiration
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const requestUrl = String(error?.config?.url || '');
        const isPasswordChangeRequest = requestUrl.includes('/auth/password/change');

        // Show toast for all API errors except 401 (handled below with redirect)
        if (error.response && (error.response.status !== 401 || isPasswordChangeRequest)) {
          const data = error.response.data;
          let message: string;
          if (Array.isArray(data?.message)) {
            message = data.message.join(', ');
          } else if (typeof data?.message === 'string') {
            message = data.message;
          } else {
            message = 'An unexpected error occurred';
          }
          toast.error(message);
        }

        if (error.response?.status === 401 && !isPasswordChangeRequest) {
          notificationService.setAuthState(false);
          // Clear auth data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('tenant');
          delete axios.defaults.headers.common['Authorization'];
          setIsAuthenticated(false);
          setUser(null);
          setTenant(null);

          // Redirect to login with message
          const currentPath = window.location.pathname;
          const publicAuthPaths = ['/login', '/activate', '/register', '/invite/complete', '/forgot-password', '/reset-password', '/accedi'];
          const isPublicAuthPath = publicAuthPaths.some((path) => currentPath.startsWith(path));
          if (!isPublicAuthPath) {
            // Store a session expired flag
            sessionStorage.setItem('sessionExpired', 'true');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const tenantData = localStorage.getItem('tenant');
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
      if (tenantData) {
        setTenant(JSON.parse(tenantData));
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      notificationService.setAuthState(true);
    } else {
      notificationService.setAuthState(false);
    }
    setIsAuthReady(true);
  }, []);

  const applyAuthPayload = (payload: { access_token: string; user: User; tenant?: Tenant | null }) => {
    localStorage.setItem('token', payload.access_token);
    localStorage.setItem('user', JSON.stringify(payload.user));
    if (payload.tenant) {
      localStorage.setItem('tenant', JSON.stringify(payload.tenant));
      setTenant(payload.tenant);
    } else {
      localStorage.removeItem('tenant');
      setTenant(null);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${payload.access_token}`;
    setIsAuthenticated(true);
    setUser(payload.user);
    notificationService.setAuthState(true);
  };

  const loginWithCredentials = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });
      applyAuthPayload(response.data);
    } catch (error) {
      throw error;
    }
  };

  const login = async (token: string) => {
    try {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Fetch user data with the token
      const response = await axios.get(`${API_URL}/users/me`);
      const userData = response.data;

      localStorage.setItem('user', JSON.stringify(userData));
      setIsAuthenticated(true);
      setUser(userData);

      // Fetch tenant data if user has tenantId
      if (userData.tenantId) {
        try {
          const tenantResponse = await axios.get(`${API_URL}/tenants/${userData.tenantId}`);
          const tenantData = tenantResponse.data;
          localStorage.setItem('tenant', JSON.stringify(tenantData));
          setTenant(tenantData);
        } catch {
          // Tenant fetch failed, continue without branding
        }
      }
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      throw error;
    }
  };

  const logout = async () => {
    // Try server-side logout to clear HttpOnly SSE cookie
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // ignore errors
    }

    try {
      notificationService.stop();
    } catch {}

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
    setTenant(null);
    notificationService.setAuthState(false);
  };

  // Update tenant locally (for settings page)
  const updateTenant = (updates: Partial<Tenant>) => {
    if (tenant) {
      const updatedTenant = { ...tenant, ...updates };
      setTenant(updatedTenant);
      localStorage.setItem('tenant', JSON.stringify(updatedTenant));
    }
  };

  // Refresh tenant data from server
  const refreshTenant = async () => {
    if (user?.tenantId) {
      try {
        const response = await axios.get(`${API_URL}/tenants/${user.tenantId}`);
        const tenantData = response.data;
        setTenant(tenantData);
        localStorage.setItem('tenant', JSON.stringify(tenantData));
      } catch {
        // Failed to refresh tenant
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthReady,
      isAuthenticated,
      user,
      tenant,
      applyAuthPayload,
      login,
      loginWithCredentials,
      logout,
      updateTenant,
      refreshTenant,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
